import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * PWA install button.
 *  - Android/Chrome/Edge: when `beforeinstallprompt` fires we trigger the
 *    native A2HS dialog. If the event never fired (no SW yet, low engagement,
 *    preview env) we fall back to a modal with manual instructions.
 *  - iOS Safari (or iOS Chrome/Firefox, which share WebKit but cannot prompt):
 *    we show a modal explaining how to "Add to Home Screen" from Safari.
 *  - Desktop browsers without prompt: the button is hidden.
 *  - Already installed (running standalone): hidden.
 *
 * The modal is portalled to document.body so it always escapes parent
 * stacking contexts (e.g. sticky sidebar).
 */
export default function InstallPWAButton({ variant = "sidebar" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [openModal, setOpenModal] = useState(null); // 'ios' | 'android' | null

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid;
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Hide if already running as installed PWA
  if (installed || isStandalone) return null;

  // Show on: any mobile device, or desktop browsers that have offered a prompt.
  if (!deferredPrompt && !isMobile) return null;

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    if (isIOS) setOpenModal("ios");
    else if (isAndroid) setOpenModal("android");
  };

  const baseBtn =
    variant === "header"
      ? "inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition"
      : "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition";

  return (
    <>
      <button onClick={handleClick} data-testid="install-pwa-btn" className={baseBtn}>
        <i className="fa-solid fa-mobile-screen-button"></i>
        <span>Instalar app</span>
      </button>

      {openModal && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4 animate-fade-in-up"
          onClick={() => setOpenModal(null)}
          data-testid={`${openModal}-install-modal`}
        >
          <div
            className="bg-white rounded-3xl shadow-xl w-full max-w-md p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-extrabold text-xl text-slate-900">Instalar KibliApp</h3>
              <button
                onClick={() => setOpenModal(null)}
                className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-900"
                data-testid="close-install-modal"
                aria-label="Cerrar"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            {openModal === "ios" ? <IOSSteps /> : <AndroidSteps />}

            <button
              onClick={() => setOpenModal(null)}
              data-testid="install-modal-ok-btn"
              className="btn-primary w-full"
            >
              Entendido
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold flex-shrink-0">{n}</span>
      <span className="text-slate-700">{children}</span>
    </li>
  );
}

function IOSSteps() {
  return (
    <>
      <p className="text-slate-600">
        Para instalar KibliApp en tu iPhone o iPad, ábrela en <span className="font-semibold">Safari</span> y sigue estos pasos:
      </p>
      <ol className="space-y-3">
        <Step n={1}>
          Toca el botón{" "}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-900 font-semibold">
            <i className="fa-solid fa-arrow-up-from-square text-blue-600"></i> Compartir
          </span>{" "}
          en la barra de Safari.
        </Step>
        <Step n={2}>
          Desliza hacia abajo y elige{" "}
          <span className="font-semibold">"Añadir a pantalla de inicio"</span>{" "}
          <i className="fa-solid fa-square-plus text-slate-500"></i>.
        </Step>
        <Step n={3}>
          Pulsa <span className="font-semibold">Añadir</span> y listo: tendrás el icono de KibliApp en tu pantalla.
        </Step>
      </ol>
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3">
        <i className="fa-solid fa-circle-info mr-1"></i>
        Una vez instalada, abre KibliApp desde el icono — no desde Safari — para una experiencia a pantalla completa.
      </div>
    </>
  );
}

function AndroidSteps() {
  return (
    <>
      <p className="text-slate-600">
        Para instalar KibliApp en tu Android desde <span className="font-semibold">Chrome</span>:
      </p>
      <ol className="space-y-3">
        <Step n={1}>
          Toca el menú{" "}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-900 font-semibold">
            <i className="fa-solid fa-ellipsis-vertical text-blue-600"></i>
          </span>{" "}
          (tres puntos) arriba a la derecha.
        </Step>
        <Step n={2}>
          Elige{" "}
          <span className="font-semibold">"Instalar app"</span>{" "}
          o <span className="font-semibold">"Añadir a pantalla de inicio"</span>{" "}
          <i className="fa-solid fa-square-plus text-slate-500"></i>.
        </Step>
        <Step n={3}>
          Confirma con <span className="font-semibold">Instalar</span> y el icono aparecerá en tu pantalla principal.
        </Step>
      </ol>
      <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3">
        <i className="fa-solid fa-circle-info mr-1"></i>
        Si no ves la opción, navega un poco por la app y vuelve a intentarlo: Chrome necesita confirmar que es una PWA válida.
      </div>
    </>
  );
}
